import React, {useMemo} from "react";
import "chartjs-adapter-date-fns";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    ArcElement,
    PointElement,
    TimeScale,
    Tooltip
} from "chart.js";
import {Bar, Line, Pie} from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler,
    TimeScale
);

function cssVar(name, fallback) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}

function fmtTick(ms) {
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function useEngineTokens() {
    return useMemo(() => ({
        grid: cssVar("--chart-grid", "rgba(0,0,0,0.07)"),
        axis: cssVar("--chart-axis", "rgba(0,0,0,0.30)"),
        text: cssVar("--chart-text", "rgba(0,0,0,0.78)"),
        c1: cssVar("--chart-1", "#26a69a"),
        c2: cssVar("--chart-2", "#64b5f6"),
        danger: cssVar("--chart-danger", "#ef5350"),
    }), []);
}

function EngineCard({title, height = 400, children, styleConfig = {}}) {
    const cardBackground = styleConfig.cardBackground || "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)";
    const cardBorder = styleConfig.borderColor || "rgba(25, 118, 210, 0.16)";
    const titleColor = styleConfig.titleColor || "#17324d";
    const plotBackground = styleConfig.plotBackground || "#ffffff";
    return (
        <div style={{
            background: cardBackground,
            border: `1px solid ${cardBorder}`,
            borderRadius: 16,
            padding: 14,
            overflow: "hidden",
            minWidth: 0,
            boxShadow: "0 8px 24px rgba(9, 30, 66, 0.08)"
        }}>
            <div style={{fontWeight: 800, fontSize: 15, marginBottom: 10, color: titleColor}}>{title}</div>
            <div style={{height, minWidth: 0, background: plotBackground, borderRadius: 10, padding: 6}}>{children}</div>
        </div>
    );
}

export function EngineLineChart({title, series, realTimeScale = true, height, styleConfig = {}}) {
    const tokens = useEngineTokens();
    const palette = useMemo(() => [tokens.c1, tokens.c2, tokens.danger], [tokens]);
    const chartText = styleConfig.textColor || tokens.text;
    const chartGrid = styleConfig.gridColor || tokens.grid;
    const chartAxis = styleConfig.axisColor || tokens.axis;

    const data = useMemo(() => {
        if (realTimeScale) {
            return {
                datasets: series.map((s, idx) => ({
                    label: s.label,
                    data: s.points,
                    parsing: false,
                    borderColor: s.color || palette[idx % palette.length],
                    backgroundColor: s.color || palette[idx % palette.length],
                    pointRadius: 0,
                    borderWidth: s.lineWidth || 2,
                    tension: 0.25,
                    spanGaps: true,
                    borderDash: s.lineStyle === "dotted" ? [2, 4] : (s.lineStyle === "dashed" || s.dashed ? [6, 4] : undefined),
                }))
            };
        }

        const labels = (series[0]?.points || []).map((p) => fmtTick(p.x));
        return {
            labels,
            datasets: series.map((s, idx) => ({
                label: s.label,
                data: s.points.map((p) => p.y),
                borderColor: s.color || palette[idx % palette.length],
                backgroundColor: s.color || palette[idx % palette.length],
                pointRadius: 0,
                borderWidth: s.lineWidth || 2,
                tension: 0.25,
                spanGaps: true,
                borderDash: s.lineStyle === "dotted" ? [2, 4] : (s.lineStyle === "dashed" || s.dashed ? [6, 4] : undefined),
            }))
        };
    }, [series, realTimeScale, palette]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        normalized: true,
        interaction: {mode: "index", intersect: false},
        plugins: {
            legend: {
                position: "top",
                labels: {color: chartText, boxWidth: 10, boxHeight: 10, padding: 14}
            },
            decimation: {
                enabled: true,
                algorithm: "lttb",
                samples: 120
            },
        },
        scales: {
            x: realTimeScale ? {
                type: "time",
                time: {unit: "day"},
                ticks: {color: chartText, autoSkip: true, maxTicksLimit: 12},
                grid: {color: chartGrid},
                border: {color: chartAxis}
            } : {
                type: "category",
                ticks: {color: chartText, autoSkip: true, maxRotation: 0, maxTicksLimit: 12},
                grid: {color: chartGrid},
                border: {color: chartAxis}
            },
            y: {
                ticks: {color: chartText},
                grid: {color: chartGrid},
                border: {color: chartAxis},
                min: 0
            }
        }
    }), [chartAxis, chartGrid, chartText, realTimeScale]);

    return (
        <EngineCard title={title} height={height} styleConfig={styleConfig}>
            {series?.length === 0 ? (
                <div style={{color: "var(--tt-muted, #666)", padding: 10}}>Nincs adat.</div>
            ) : (
                <Line key={realTimeScale ? "time" : "cat"} data={data} options={options}/>
            )}
        </EngineCard>
    );
}

export function EngineBarChart({title, labels, series, height, styleConfig = {}}) {
    const tokens = useEngineTokens();
    const palette = useMemo(() => [tokens.danger, "#efb2b2", "green", "#b2d8b2"], [tokens]);
    const chartText = styleConfig.textColor || tokens.text;
    const chartGrid = styleConfig.gridColor || tokens.grid;
    const chartAxis = styleConfig.axisColor || tokens.axis;

    const data = useMemo(() => ({
        labels,
        datasets: series.map((s, idx) => ({
            label: s.label,
            data: s.data,
            backgroundColor: s.color || palette[idx % palette.length],
            borderColor: s.color || palette[idx % palette.length],
            borderWidth: s.lineWidth || 1,
            borderDash: s.lineStyle === "dotted" ? [2, 4] : (s.lineStyle === "dashed" ? [6, 4] : undefined),
            stack: s.stack
        }))
    }), [labels, series, palette]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        normalized: true,
        plugins: {
            legend: {
                position: "top",
                labels: {color: chartText, boxWidth: 10, boxHeight: 10, padding: 14}
            }
        },
        scales: {
            x: {
                stacked: true,
                ticks: {color: chartText, autoSkip: true, maxRotation: 0, maxTicksLimit: 12},
                grid: {color: chartGrid},
                border: {color: chartAxis}
            },
            y: {
                stacked: true,
                ticks: {color: chartText},
                grid: {color: chartGrid},
                border: {color: chartAxis},
                min: 0
            }
        }
    }), [chartAxis, chartGrid, chartText]);

    return (
        <EngineCard title={title} height={height} styleConfig={styleConfig}>
            {labels?.length === 0 || series?.length === 0 ? (
                <div style={{color: "var(--tt-muted, #666)", padding: 10}}>Nincs adat.</div>
            ) : (
                <Bar data={data} options={options}/>
            )}
        </EngineCard>
    );
}

export function EnginePieChart({title, series, height, styleConfig = {}}) {
    const tokens = useEngineTokens();
    const chartText = styleConfig.textColor || tokens.text;

    const pieSeries = useMemo(() => (series || []).filter((s) => Number(s.value) > 0), [series]);
    const data = useMemo(() => ({
        labels: pieSeries.map((s) => s.label),
        datasets: [{
            data: pieSeries.map((s) => Number(s.value) || 0),
            backgroundColor: pieSeries.map((s) => s.color),
            borderColor: styleConfig.plotBackground || "#ffffff",
            borderWidth: 1
        }]
    }), [pieSeries, styleConfig.plotBackground]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            legend: {
                position: "right",
                labels: {color: chartText, boxWidth: 10, boxHeight: 10, padding: 10}
            }
        }
    }), [chartText]);

    return (
        <EngineCard title={title} height={height} styleConfig={styleConfig}>
            {pieSeries.length === 0 ? (
                <div style={{color: "var(--tt-muted, #666)", padding: 10}}>Nincs adat.</div>
            ) : (
                <Pie data={data} options={options}/>
            )}
        </EngineCard>
    );
}

